// Helpers compartilhados pelos downloaders offline (HLS, PDF, futuros tipos).
// Centraliza convenção de path/sanitização pra evitar drift entre serviços.

import { Filesystem, Directory } from '@capacitor/filesystem';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';

// Sandbox interno do app — no iOS mapeia pra Documents (via @capacitor/filesystem v8),
// no Android pra Context.getFilesDir(). Mesma raiz onde o plugin nativo
// VxlBatchDownloader escreve, então Filesystem.getUri({directory: Data})
// resolve pros mesmos arquivos.
export const STORAGE_DIR = Directory.Data;
export const OFFLINE_DIR = 'offline';

// Metadados offline (estrutura/grids/enrolls/imageMap) num arquivo único. Vive
// em `offline/_meta/` — o underscore garante que nunca colide com `offline/<loId>/`
// (loIds reais são numéricos e `safeLoId` só produz alfanuméricos/_/-).
export const META_DIR = `${OFFLINE_DIR}/_meta`;
export const STRUCTURE_FILE = `${META_DIR}/structure.json`;

// Sanitiza loId pra uso seguro em path/batchId. Aceita só alfanuméricos e
// `_`/`-`. Defesa em profundidade contra path traversal (loIds reais do
// backend são numéricos, mas validação local elimina o vetor independente).
export function safeLoId (loId) {
  if (loId == null) return '';
  return String(loId).replace(/[^A-Za-z0-9_-]/g, '_');
}

// Diretório de destino por LO (compartilhado entre tipos: HLS escreve
// seg_*.ts + manifest.m3u8; PDF escreve document.pdf no mesmo lugar).
export function destDirFor (loId) {
  return `${OFFLINE_DIR}/${safeLoId(loId)}`;
}

// Arquivo de cache de IA (resumo/mapa mental/quiz) de um LO. Vive DENTRO do
// destDir do LO de propósito: o `rmdir` recursivo de remove() apaga junto, sem
// cleanup extra.
export function aiFileFor (loId) {
  return `${destDirFor(loId)}/ai.json`;
}

// Arquivo de cache da transcrição (legendas/frases) de um LO. Mesma convenção do
// ai.json — dentro do destDir, apagado de graça no rmdir da remoção.
export function transcriptFileFor (loId) {
  return `${destDirFor(loId)}/transcript.json`;
}

// mkdir idempotente — não falha se já existe.
export async function ensureDir (path) {
  try {
    await Filesystem.mkdir({ path, directory: STORAGE_DIR, recursive: true });
  } catch {
    // já existe
  }
}

// Soma bytes de todos os arquivos do diretório offline de um LO. Usado pra
// mostrar tamanho real (não estimado) na UI de "Meus Downloads" e nos totais
// agregados por módulo/curso. Não-recursivo: o diretório tem todos os arquivos
// no top-level (segmentos .ts + manifest.m3u8 do HLS, document.pdf do PDF).
// Retorna 0 se o diretório não existe ou em qualquer falha de I/O — UI deve
// tratar 0 como "tamanho desconhecido" e omitir o badge.
export async function computeOfflineSize (loId) {
  const dir = destDirFor(loId);
  // Caminho rápido: soma nativa (1 número pelo bridge). O Filesystem.readdir
  // enumera todos os arquivos com metadados — com centenas de segmentos HLS,
  // marshalar isso travava a finalização do download por segundos.
  try {
    const { bytes } = await VxlBatchDownloader.getDirectorySize({ path: dir });
    // `> 0` (não `>= 0`): se o nativo devolver 0 por path-mismatch (ex.:
    // Directory.Data não mapeando pra getFilesDir num OEM), deixa cair no
    // readdir abaixo em vez de aceitar um 0 falso. Diretório legítimo vazio
    // também devolve 0 no readdir → mesmo resultado, sem risco de badge sumir.
    if (typeof bytes === 'number' && bytes > 0) return bytes;
  } catch {
    // plugin indisponível (web/dev) ou método ausente num build nativo antigo
    // — cai pro readdir abaixo.
  }
  try {
    const result = await Filesystem.readdir({ path: dir, directory: STORAGE_DIR });
    return (result.files || []).reduce((acc, f) => acc + (f.size || 0), 0);
  } catch {
    return 0;
  }
}
